import { type FC } from 'react';
import { useForm } from 'react-hook-form';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from '@cencosud-cencommerce/paris-uikit';
import { yupResolver } from '@hookform/resolvers/yup';
import type { ObjectSchema } from 'yup';

import {
  isDniFeatureEnabled,
  isDniRegisteredFeatureEnabled
} from '@/config/featureFlags';
import type { IPickupId, IPickupIdForm } from '@/Domain/PickupId';
import { getPickupIdFormSchema } from '@/Domain/PickupId';
import {
  type IdentificationDocumentType,
  IdentificationDocumentTypes
} from '@/modules/shared/types/IdentificationDocumentType';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import type { GtmData } from '@/modules/shared/utils/gtmEvents/types';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories,
  ShippingEventInteractiveTags
} from '@/modules/shared/utils/gtmEvents/types';
import useSetOnBehalf from '@/sections/cart/hooks/mutators/useSetOnBehalf';
import { useCustomerContext } from '@/sections/customer/CustomerContext';

import { PickupIdForm } from '../../Components/PickupIdForm';

interface Props {
  'data-testid'?: string;
  isOpen: boolean;
  onClose: VoidFunction;
  onSubmit: (pickup?: IPickupId) => void;
  deliveryGroupId: string;
  pickupForm?: IPickupIdForm;
  gtmData?: GtmData;
  customerDocumentInfo?: {
    documentType?: IdentificationDocumentType;
    documentID?: string;
    customerRut?: string;
  };
}

const PickupIdModal: FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  pickupForm,
  deliveryGroupId,
  customerDocumentInfo,
  'data-testid': dataTestId = 'paris-pickupidformmodal',
  gtmData: { groupNumber, totalGroups, sku } = {
    groupNumber: 0,
    totalGroups: 0,
    sku: ''
  }
}) => {
  const { trigger: setOnBehalf, isLoading } = useSetOnBehalf();
  const {
    customer: { data: customer }
  } = useCustomerContext();

  const isRegistered = customer !== null;
  const isDniEnabled = isRegistered
    ? isDniRegisteredFeatureEnabled()
    : isDniFeatureEnabled();

  const {
    handleSubmit,
    register,
    control,
    reset,
    watch,
    trigger,
    setValue,
    clearErrors
  } = useForm<IPickupIdForm>({
    resolver: yupResolver(
      getPickupIdFormSchema(isDniEnabled) as ObjectSchema<IPickupIdForm>
    ),
    defaultValues: {
      fullname: '',
      documentType: IdentificationDocumentTypes.RUT,
      documentID: ''
    },
    mode: 'onSubmit',
    values: pickupForm ?? {
      fullname: '',
      documentType: IdentificationDocumentTypes.RUT,
      documentID: ''
    }
  });

  const sendWhoRetrivesCloseModalEvent = () => {
    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.AnotherPerson,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag':
          ShippingEventInteractiveTags.WhoWillPikcupClose,
        'event-interactive-grupo_despacho': `${groupNumber} - ${totalGroups}`,
        'event-interactive-sku': sku
      }
    });
  };

  const handleOnClose = () => {
    sendWhoRetrivesCloseModalEvent();
    onClose();

    if (pickupForm) {
      reset(pickupForm);
    } else {
      reset({
        fullname: '',
        documentType: IdentificationDocumentTypes.RUT,
        documentID: ''
      });
    }
  };

  const fullname = watch('fullname');
  const documentType = watch('documentType');
  const documentID = watch('documentID');
  const effectiveDocumentType = isDniEnabled
    ? documentType
    : IdentificationDocumentTypes.RUT;

  return (
    <form
      onSubmit={handleSubmit(async (pickupForm) => {
        try {
          const sanitizedDocumentType = isDniEnabled
            ? pickupForm.documentType
            : IdentificationDocumentTypes.RUT;
          const sanitizedPickup = {
            ...pickupForm,
            documentType: sanitizedDocumentType
          };

          await setOnBehalf({
            deliveryGroupId: deliveryGroupId,
            authorizedPerson: {
              fullname: sanitizedPickup.fullname,
              documentType: sanitizedPickup.documentType,
              documentID: sanitizedPickup.documentID
            },
            customerDocumentInfo
          });

          SendShippingEvents.sendEvent({
            event: 'baseEvent',
            extraFields: {
              'event-interactive-tag':
                ShippingEventInteractiveTags.WhoWillPikcupConfirm,
              'event-interactive-accion':
                ShippingEventInteractiveActions.AnotherPerson,
              'event-interactive-grupo_despacho': `${groupNumber} - ${totalGroups}`,
              documentType: sanitizedPickup?.documentType
            }
          });
          onSubmit(sanitizedPickup);
        } catch (error) {
          onClose();
        }
      })}
    >
      <Modal isOpen={isOpen} data-testid={dataTestId} size="sm">
        <ModalHeader
          onClose={handleOnClose}
          icon={{ name: 'person', fill: 'info-500' }}
          title="¿Quién hará el retiro?"
        />
        <ModalBody>
          <PickupIdForm
            register={register}
            control={control}
            trigger={trigger}
            setValue={setValue}
            clearErrors={clearErrors}
            isDniEnabled={isDniEnabled}
          />
        </ModalBody>
        <ModalFooter
          primaryButton={{
            label: 'Confirmar',
            type: 'submit',
            loading: isLoading,
            disabled: !fullname || !effectiveDocumentType || !documentID
          }}
        />
      </Modal>
    </form>
  );
};

export default PickupIdModal;
